/**
 * The product page's condition summary (MERCH-57): the few facts a shopper weighs
 * before buying an item (its condition label first, D98; grade, battery and the
 * inspection only where the condition declares them), from data the page already loads — the
 * listing row, its public inspection passport (lib/catalog/inspections
 * publicPassport) and the sfSeller terms. Pure: no I/O, no React.
 *
 * A row is left out when the data does not exist; nothing is guessed. There is
 * no delivery row: neither the listing nor the seller feed carries a delivery
 * estimate (it needs the shopper's PIN, see components/PinCheck).
 */
import { GRADE_LABEL, batteryApplies, batteryShown, conditionBadge, gradeApplies, inspectionApplies } from './listing';
import { returnsLabel } from './terms';

// The passport group that holds cosmetic checks (lib/catalog/checklists `physical`).
const COSMETIC_GROUP = 'Physical condition';

/** Notes the technician left on the cosmetic checks, in report order. */
export function cosmeticNotes(report) {
  const groups = (report && report.groups) || [];
  return groups
    .filter((g) => g.category === COSMETIC_GROUP)
    .flatMap((g) => g.items.map((i) => String(i.note || '').trim()))
    .filter(Boolean);
}

function withNoteCount(report) {
  if (Number.isInteger(report.passed_with_note)) return report.passed_with_note;
  return (report.groups || []).reduce((n, g) => n + g.items.filter((i) => i.result === 'pass_with_note').length, 0);
}

function inspectionValue(insp) {
  const r = insp && insp.data;
  if (r) {
    const notes = withNoteCount(r);
    return `${r.passed}/${r.total} passed${notes ? ` · ${notes} with a note` : ''}`;
  }
  if (insp && insp.loading) return 'Loading…';
  if (insp && insp.error) return 'Report unavailable';
  return 'Not inspected yet';
}

/**
 * [{ key, label, value }] in display order. `insp` and `seller` are the page's
 * queries ({ data, loading, error }).
 */
export function conditionSummary(l, insp, seller) {
  const r = insp && insp.data;
  // Grade and battery only where the item's condition declares them (D98); rows without the flags are refurbished-era.
  const battery = !batteryApplies(l) ? null : batteryShown(l) ? l.battery_health_pct : (r && r.battery ? r.battery.health_pct : null);
  const notes = cosmeticNotes(r);
  // A condition without an inspection gate (e.g. new) shows the row only when a report exists.
  const showInspection = inspectionApplies(l) || Boolean(r);
  return [
    { key: 'condition', label: 'Condition', value: conditionBadge(l) || 'Not specified' },
    gradeApplies(l) ? { key: 'grade', label: 'Grade', value: l.grade ? GRADE_LABEL[l.grade] || `Grade ${l.grade}` : 'Not graded' } : null,
    battery != null ? { key: 'battery', label: 'Battery health', value: `${battery}%` } : null,
    notes.length ? { key: 'cosmetic', label: 'Cosmetic notes', value: notes.join('; ') } : null,
    showInspection ? { key: 'inspection', label: 'Inspection', value: inspectionValue(insp) } : null,
    { key: 'warranty', label: 'Warranty', value: l.warranty_months ? `${l.warranty_months} months` : 'Not specified' },
    { key: 'returns', label: 'Returns', value: returnsLabel(seller && seller.data) },
  ].filter(Boolean);
}
